import { useCallback, useEffect, useState } from 'react';
import { api, formatWib } from '../lib/api';
import { TombolIsiPratayang } from '../components/TombolIsiPratayang';
import { useBahasa } from '../i18n';
import { RETENSI_MAX_HARI } from '../lib/retensi';
import { CameraTemplates } from '../components/CameraTemplates';
import { MaintenanceSwitch } from '../components/MaintenanceSwitch';

interface Health {
  worker: { ok: boolean; heartbeatAgeS: number | null; enabledCameras: number };
  recordingRoot: {
    path: string;
    source: 'setting' | 'default';
    ok: boolean;
    reason: string | null;
    freeGb: number | null;
    totalGb: number | null;
    // Penilaian worker sendiri (§B4.1). `null` = worker tidak melapor dalam 60
    // detik terakhir — bukan berarti sehat.
    worker: { path: string; ok: boolean; reason: string | null; ageS: number } | null;
  };
  hwaccel: { backend: string; decode: boolean; encode: boolean; summary: string } | null;
  storage: {
    sampleDays: number;
    perCamera: {
      cameraId: string;
      name: string;
      isEnabled: boolean;
      maxDay: number;
      /** `true` = kamera ini ikut angka global, tidak menyetel sendiri (§D6). */
      maxDayInherited: boolean;
      segments: number;
      sampleSince: string | null;
      measuredMbps: number | null;
      gbPerDay: number | null;
      projectedGb: number | null;
    }[];
    totalGbPerDay: number;
    projectedGb: number;
    freeGb: number | null;
    daysUntilFull: number | null;
    overCapacity: boolean;
    suggestedMaxDay: number | null;
    retentionDays: number;
    /** §D6 poin 6 — `null` berarti jatah disk tidak ditetapkan. */
    quota: {
      gb: number;
      usedGb: number;
      usedPercent: number;
      thresholdPercent: number;
    } | null;
  };
}

interface Settings {
  editable: {
    recordingRoot: {
      value: string | null;
      effective: string;
      source: 'setting' | 'default';
      default: string;
    };
    cameraLogRetentionDays: { value: number | null; default: number };
    retentionDays: { value: number | null; default: number };
    /** §D2 — panjang berkas rekaman bawaan, dalam detik. */
    segmentSeconds: { value: number | null; default: number };
    maxDiskGb: { value: number | null; default: null };
    /** §D8.1 — auto scan pratayang saat kameranya tidak sedang dibuka orang. */
    spriteAutoScan: { value: boolean | null; default: boolean };
  };
  readOnly: {
    hwaccel: { mode: string; device: string; maxSessions: number; fallback: boolean };
    stream: { idleTimeoutS: number; maxConcurrent: number };
    ldap: { enabled: boolean; url: string | null; mode: string };
    source: string;
  };
}

interface AuditRow {
  id: string;
  username: string;
  action: string;
  tableName: string | null;
  recordId: string | null;
  detail: unknown;
  ipAddress: string | null;
  createdAt: string;
}

function Section({
  title,
  children,
  collapsible = false,
  defaultOpen = false,
  ringkas,
}: {
  title: string;
  children: React.ReactNode;
  /**
   * Kartu yang isinya bisa panjang tak terbatas dilipat (23 Agu 2026).
   *
   * Dua kartu di halaman ini tumbuh seturut data, bukan seturut desain:
   * Storage forecast satu baris per kamera (96 dan bertambah) dan Audit log 25
   * baris. Membatasi tingginya lalu menggulung di dalam kartu — yang dipasang
   * 23 Agu pagi — memang menyelamatkan halaman, tapi keduanya tetap memakan
   * ±15rem sebelum satu pun setelan yang bisa diubah terlihat. Setelan adalah
   * isi halaman ini; laporan hanya bukti pendukungnya, jadi laporan yang
   * dilipat.
   *
   * Bukan `<details>`: ringkasannya harus bisa memuat angka yang ikut berubah
   * dan kepala kartunya sudah punya bentuknya sendiri di seluruh halaman.
   */
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Satu kalimat yang tetap terbaca saat terlipat — isi kartu tanpa membukanya. */
  ringkas?: React.ReactNode;
}) {
  const [terbuka, setTerbuka] = useState(defaultOpen);

  if (!collapsible) {
    return (
      <section className="mb-6 rounded-lg border border-line bg-surface p-4">
        <h2 className="mb-3 text-lg">{title}</h2>
        {children}
      </section>
    );
  }

  return (
    <section className="mb-6 rounded-lg border border-line bg-surface">
      <h2>
        <button
          type="button"
          onClick={() => setTerbuka((v) => !v)}
          aria-expanded={terbuka}
          className="flex min-h-tap w-full items-center gap-2 rounded-lg p-4 text-left transition-colors hover:bg-subtle"
        >
          <span className="text-lg">{title}</span>
          {ringkas && <span className="min-w-0 flex-1 truncate text-sm text-muted">{ringkas}</span>}
          <span
            aria-hidden="true"
            className={`ml-auto shrink-0 text-muted transition-transform duration-200 ease-out motion-reduce:transition-none ${
              terbuka ? 'rotate-90' : ''
            }`}
          >
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="m9 6 6 6-6 6" />
            </svg>
          </span>
        </button>
      </h2>

      {terbuka && <div className="border-t border-line p-4">{children}</div>}
    </section>
  );
}

/**
 * Proyeksi storage sebagai keluaran shell (§D5.1, 23 Agustus 2026).
 *
 * Diminta lapangan dengan alasan yang lebih dalam daripada selera: *"Malah
 * kayaknya seru kalo bentuknya kayak putty cli/shell aja terus bisa update
 * gitu. Lebih enak kalo ada grafiknya mode shell."* Yang benar-benar ditolongnya
 * adalah **membandingkan** — pada 94 baris, tabel HTML memaksa mata melompat
 * antar kolom yang lebarnya berubah-ubah, sementara kolom monospasi yang
 * sejajar plus batang ASCII menjawab "siapa yang paling rakus" dalam sekali
 * lihat. Bukan hiasan: batangnya membawa satu angka yang tabelnya tidak pernah
 * bisa tunjukkan sekaligus untuk 94 baris.
 *
 * Bentuk tabel **tidak** dibuang. Ia yang bisa disalin ke spreadsheet dan yang
 * dibaca pembaca layar dengan benar; konsol ini alternatif, bukan pengganti,
 * dan pilihannya diingat di `localStorage`.
 */
const KUNCI_TAMPILAN_FORECAST = 'lensa.admin.forecastView';

function BatangAscii({ nilai, maks, lebar = 20 }: { nilai: number; maks: number; lebar?: number }) {
  // Minimal satu blok untuk apa pun yang bukan nol: baris yang benar-benar
  // kosong terbaca sebagai "tidak ada data", padahal artinya "sangat kecil".
  const penuh = maks > 0 ? Math.max(nilai > 0 ? 1 : 0, Math.round((nilai / maks) * lebar)) : 0;
  return (
    <span aria-hidden="true">
      {'█'.repeat(penuh)}
      <span className="opacity-30">{'░'.repeat(Math.max(0, lebar - penuh))}</span>
    </span>
  );
}

function KonsolForecast({ s }: { s: Health['storage'] }) {
  /**
   * Jam yang berdetak, satu-satunya alasan "bisa update gitu" terpenuhi tanpa
   * berbohong: angkanya sendiri datang dari polling 30 detik milik halaman ini,
   * dan konsol yang diam total sesudah dibuka terbaca seperti tangkapan layar.
   */
  const [jam, setJam] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setJam(new Date()), 1_000);
    return () => window.clearInterval(t);
  }, []);

  // Terbesar dulu: pertanyaan yang dibawa orang ke halaman ini hampir selalu
  // "siapa yang menghabiskan disk", bukan "apa kabar kamera berhuruf A".
  const baris = [...s.perCamera]
    .filter((c) => c.gbPerDay !== null)
    .sort((a, b) => (b.gbPerDay ?? 0) - (a.gbPerDay ?? 0));
  const maks = baris.reduce((m, c) => Math.max(m, c.gbPerDay ?? 0), 0);
  const lebarNama = Math.min(28, baris.reduce((m, c) => Math.max(m, c.name.length), 0));

  const pad = (t: string, n: number) => (t.length > n ? `${t.slice(0, n - 1)}…` : t.padEnd(n));
  const kanan = (t: string, n: number) => t.padStart(n);

  return (
    // Tingginya dipatok dan digulung di dalam kotaknya sendiri, alasan yang
    // sama persis dengan tabelnya: 92 baris monospasi ±1.900px, dan kartu yang
    // isinya bisa panjang tak terbatas harus membatasi dirinya sendiri, bukan
    // membebankan panjangnya ke halaman.
    <div className="max-h-[26rem] overflow-auto rounded-md border border-line bg-[#0b0f14] p-3 text-[13px] leading-[1.45] text-[#c8d3e0]">
      <pre className="min-w-max font-mono">
        <span className="text-[#7ee787]">lensa@storage</span>
        <span className="text-[#8b98a5]">:</span>
        <span className="text-[#79c0ff]">~</span>
        <span className="text-[#8b98a5]">$ </span>
        forecast --per-camera --sample={s.sampleDays}d
        {'\n'}
        <span className="text-[#8b98a5]">
          {`# ${jam.toLocaleTimeString('en-GB')} WIB · retention default ${s.retentionDays}d · ${baris.length} cameras reporting`}
        </span>
        {'\n\n'}
        <span className="text-[#8b98a5]">
          {`${pad('CAMERA', lebarNama)}  ${kanan('Mbps', 6)}  ${kanan('GB/day', 7)}  ${kanan('KEEP', 5)}  ${kanan('FORECAST', 9)}  USAGE`}
        </span>
        {'\n'}
        {baris.map((c) => (
          <span key={c.cameraId}>
            {pad(c.name, lebarNama)}
            {'  '}
            {kanan(c.measuredMbps === null ? '—' : c.measuredMbps.toFixed(2), 6)}
            {'  '}
            {kanan((c.gbPerDay ?? 0).toFixed(1), 7)}
            {'  '}
            <span className={c.maxDayInherited ? 'text-[#8b98a5]' : ''}>
              {kanan(`${c.maxDay}d`, 5)}
            </span>
            {'  '}
            {kanan(c.projectedGb === null ? '—' : `${c.projectedGb} GB`, 9)}
            {'  '}
            <span className="text-[#58a6ff]">
              <BatangAscii nilai={c.gbPerDay ?? 0} maks={maks} />
            </span>
            {!c.isEnabled && <span className="text-[#8b98a5]"> (disabled)</span>}
            {'\n'}
          </span>
        ))}
        {'\n'}
        <span className="text-[#8b98a5]">{'─'.repeat(lebarNama + 40)}</span>
        {'\n'}
        {pad('TOTAL', lebarNama)}
        {'  '}
        {kanan('', 6)}
        {'  '}
        <span className="text-[#f0f6fc]">{kanan(s.totalGbPerDay.toFixed(1), 7)}</span>
        {'  '}
        {kanan('', 5)}
        {'  '}
        <span className="text-[#f0f6fc]">{kanan(`${s.projectedGb} GB`, 9)}</span>
        {'\n\n'}
        {s.freeGb !== null && (
          <>
            <span className={s.overCapacity ? 'text-[#ffa657]' : 'text-[#7ee787]'}>
              {s.overCapacity ? '! ' : '✓ '}
            </span>
            <span className={s.overCapacity ? 'text-[#ffa657]' : ''}>
              {s.overCapacity
                ? `forecast ${s.projectedGb} GB exceeds ${s.freeGb.toFixed(1)} GB free${
                    s.suggestedMaxDay !== null ? ` — try retention ${s.suggestedMaxDay}d` : ''
                  }`
                : `forecast ${s.projectedGb} GB fits in ${s.freeGb.toFixed(1)} GB free`}
            </span>
            {'\n'}
          </>
        )}
        {s.quota && (
          <>
            <span className="text-[#8b98a5]">{'  quota  '}</span>
            <span className={s.quota.usedPercent >= s.quota.thresholdPercent ? 'text-[#ffa657]' : 'text-[#58a6ff]'}>
              <BatangAscii nilai={s.quota.usedPercent} maks={100} lebar={24} />
            </span>
            {`  ${s.quota.usedGb.toFixed(1)} / ${s.quota.gb} GB (${s.quota.usedPercent.toFixed(1)}%, deletes at ${s.quota.thresholdPercent}%)`}
            {'\n'}
          </>
        )}
        {s.daysUntilFull !== null && (
          <>
            <span className="text-[#8b98a5]">{`  full in ~${s.daysUntilFull} days at this rate`}</span>
            {'\n'}
          </>
        )}
        <span className="text-[#7ee787]">lensa@storage</span>
        <span className="text-[#8b98a5]">:</span>
        <span className="text-[#79c0ff]">~</span>
        <span className="text-[#8b98a5]">$ </span>
        <span className="animate-pulse">▋</span>
      </pre>
    </div>
  );
}

export function AdminPage() {
  const { t } = useBahasa();
  const [health, setHealth] = useState<Health | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [audit, setAudit] = useState<AuditRow[] | null>(null);
  const [pesan, setPesan] = useState<{
    jenis: 'ok' | 'gagal' | 'peringatan';
    teks: string;
  } | null>(null);

  const [rootDraft, setRootDraft] = useState('');
  const [retensiDraft, setRetensiDraft] = useState('');
  /** §D6 — umur bawaan rekaman, dan jatah disk seluruh Lensa. */
  const [retensiRekamanDraft, setRetensiRekamanDraft] = useState('');
  const [kuotaDraft, setKuotaDraft] = useState('');
  /** §D2 — panjang segmen global, disunting dalam menit. */
  const [segmenDraft, setSegmenDraft] = useState('');
  /** §D8.1 — sakelar auto scan pratayang. `null` di server berarti belum pernah diputuskan. */
  const [autoScanDraft, setAutoScanDraft] = useState(false);
  /** Bentuk tampilan proyeksi: tabel (bawaan) atau konsol (§D5.1). */
  const [tampilanForecast, setTampilanForecast] = useState<'tabel' | 'konsol'>(() => {
    try {
      return localStorage.getItem(KUNCI_TAMPILAN_FORECAST) === 'konsol' ? 'konsol' : 'tabel';
    } catch {
      return 'tabel';
    }
  });
  const [menyimpan, setMenyimpan] = useState(false);

  /**
   * Banner hasil simpan tidak boleh menemani isian yang sudah diketik ulang.
   * Tanpa ini "Settings saved." dari simpanan sebelumnya tetap terpampang di
   * atas draft baru yang belum dikirim ke mana pun — kelihatan berhasil padahal
   * belum, dan itu yang membuat "tidak tersimpan" sulit dikenali. Dipanggil tiap
   * ketikan, jadi state hanya disentuh kalau memang masih ada yang dihapus.
   */
  const hapusPesan = useCallback(() => setPesan((p) => (p === null ? p : null)), []);

  /**
   * §D6 — retensi global di atas batas tidak bisa disimpan, dan kalimatnya
   * dibaca sebelum Simpan ditekan, bukan sesudah server menolaknya. Server tetap
   * yang menentukan; yang di sini cuma mendahuluinya.
   */
  const retensiKelewatan =
    retensiRekamanDraft.trim() !== '' && Number(retensiRekamanDraft) > RETENSI_MAX_HARI;

  /**
   * `isiDraft` sengaja tidak menyala pada polling. Isian form hanya boleh
   * ditimpa saat halaman pertama dibuka dan setelah Admin sendiri menyimpan —
   * bukan tiap 30 detik. Sebelumnya penyegaran berkala ikut menulis ulang
   * `rootDraft`, jadi path yang sedang diketik terhapus di tengah jalan dan
   * kembali ke nilai lama: tampak seperti "tersimpan lalu balik sendiri",
   * padahal tidak ada yang pernah terkirim ke server.
   */
  const muat = useCallback(async (isiDraft = false) => {
    try {
      const [h, s, a] = await Promise.all([
        api<Health>('/system/health'),
        api<Settings>('/system/settings'),
        api<{ auditLogs: AuditRow[] }>('/audit-logs?limit=25'),
      ]);
      setHealth(h);
      setSettings(s);
      setAudit(a.auditLogs);
      if (isiDraft) {
        setRootDraft(s.editable.recordingRoot.value ?? '');
        setRetensiDraft(
          s.editable.cameraLogRetentionDays.value === null
            ? ''
            : String(s.editable.cameraLogRetentionDays.value),
        );
        setRetensiRekamanDraft(
          s.editable.retentionDays.value === null ? '' : String(s.editable.retentionDays.value),
        );
        setKuotaDraft(
          s.editable.maxDiskGb.value === null ? '' : String(s.editable.maxDiskGb.value),
        );
        // Disimpan detik, disunting menit: yang diucapkan orang tentang panjang
        // rekaman selalu menit ("per 15 menit"), dan detik cuma satuan
        // penyimpanannya.
        setSegmenDraft(
          s.editable.segmentSeconds.value === null
            ? ''
            : String(Math.round(s.editable.segmentSeconds.value / 60)),
        );
        setAutoScanDraft(s.editable.spriteAutoScan.value ?? s.editable.spriteAutoScan.default);
      }
    } catch (e) {
      setPesan({ jenis: 'gagal', teks: (e as Error).message });
    }
  }, []);

  useEffect(() => {
    void muat(true);
    // Status worker & disk perlu segar, tapi tidak realtime — 30 detik cukup.
    const t = window.setInterval(() => void muat(), 30_000);
    return () => window.clearInterval(t);
  }, [muat]);

  async function simpan(body: Record<string, unknown>) {
    setMenyimpan(true);
    setPesan(null);
    try {
      const res = await api<{ ok: boolean; changed: string[]; warning: string | null }>(
        '/system/settings',
        { method: 'PUT', body: JSON.stringify(body) },
      );
      // Tersimpan tapi belum terbukti bisa ditulis (§B4.1) bukan kabar baik —
      // jangan disamarkan jadi "Settings saved." begitu saja.
      setPesan(
        res.warning
          ? { jenis: 'peringatan', teks: `${t('Settings saved.')} ${res.warning}` }
          : { jenis: 'ok', teks: t('Settings saved.') },
      );
      // Setelah simpan sendiri barulah draft diisi ulang dari server — supaya
      // "Reset to default" benar-benar mengosongkan kotaknya.
      await muat(true);
    } catch (e) {
      setPesan({ jenis: 'gagal', teks: (e as Error).message });
    } finally {
      setMenyimpan(false);
    }
  }

  /**
   * Banner dipisah jadi variabel karena dipakai DUA cabang render — termasuk
   * cabang "belum termuat" di bawah. Sebelumnya ia hanya ada di cabang normal,
   * jadi kalau `muat()` gagal pesannya di-set ke state tapi tidak pernah sampai
   * ke layar: halaman berhenti di "Loading system settings…" selamanya, dan
   * penyebabnya — DB mati, sesi habis, apa pun — tidak pernah terbaca siapa pun.
   */
  const banner = pesan && (
    <p
      role={pesan.jenis === 'gagal' ? 'alert' : 'status'}
      className={`mb-4 rounded-md border px-3 py-2 text-sm ${
        pesan.jenis === 'ok'
          ? 'border-line text-muted'
          : pesan.jenis === 'peringatan'
            ? 'border-amber-500 text-amber-600'
            : // Gagal harus terbedakan dari peringatan tanpa perlu dibaca dulu.
              // Keduanya amber membuat "ditolak" dan "tersimpan, tapi" tampak
              // sama persis.
              'border-red-500 text-red-600'
      }`}
    >
      {pesan.teks}
    </p>
  );

  if (!health || !settings) {
    return (
      <div className="mx-auto max-w-4xl p-4">
        {banner}
        {pesan?.jenis === 'gagal' ? (
          // Polling 30 detik tetap berjalan, jadi halaman pulih sendiri begitu
          // penyebabnya hilang. Yang penting jangan mengaku sedang memuat
          // padahal barusan gagal.
          <p className="text-sm text-muted" role="status">
            {t('Settings could not be loaded. Retrying every 30 seconds.')}
          </p>
        ) : (
          <p className="text-sm text-muted" role="status">
            {t('Loading settings…')}
          </p>
        )}
      </div>
    );
  }

  const s = health.storage;

  return (
    <div className="mx-auto max-w-4xl p-4">
      <h1 className="mb-4 text-xl">{t('System settings')}</h1>

      {banner}

      {/* Terlipat secara bawaan: proyeksi itu laporan, dan laporan tidak boleh
          berdiri di depan setelan yang orang datang untuk mengubahnya. Yang
          tidak boleh ikut terlipat adalah peringatannya — karena itu status
          "lebih besar daripada sisa disk" naik ke ringkasan kepala kartu, dan
          terbaca tanpa membuka apa pun. */}
      <Section
        title={t('Storage forecast')}
        collapsible
        defaultOpen={s.overCapacity}
        ringkas={
          s.perCamera.every((c) => c.measuredMbps === null) ? (
            t('No measurements yet')
          ) : s.overCapacity ? (
            <span className="text-amber-600">
              {t('{proyeksi} GB forecast — over the {sisa} GB free', {
                proyeksi: s.projectedGb,
                sisa: s.freeGb?.toFixed(1) ?? '—',
              })}
            </span>
          ) : (
            t('{perHari} GB/day · {proyeksi} GB forecast · {n} cameras', {
              perHari: s.totalGbPerDay,
              proyeksi: s.projectedGb,
              n: s.perCamera.length,
            })
          )
        }
      >
        {/* §D5.1: diukur dari data nyata, bukan ditebak dari tabel perencanaan. */}
        {s.perCamera.every((c) => c.measuredMbps === null) ? (
          <p className="text-sm text-muted">No measurements yet.</p>
        ) : (
          <>
            {s.overCapacity && (
              <p className="mb-3 rounded-md border border-amber-500 px-3 py-2 text-sm text-amber-600">
                The forecast of {s.projectedGb} GB exceeds the {s.freeGb?.toFixed(1)} GB still
                free.
                {s.suggestedMaxDay !== null && (
                  <> Lower retention to around {s.suggestedMaxDay} days to make it fit.</>
                )}
              </p>
            )}
            {/* Dua bentuk untuk data yang sama. Tabel tetap bawaan: ia yang bisa
                disalin ke spreadsheet dan yang dibacakan pembaca layar dengan
                benar. Konsol menjawab pertanyaan yang berbeda — "siapa yang
                paling rakus" — dan menjawabnya untuk 94 baris sekaligus. */}
            <div
              role="group"
              aria-label="Forecast view"
              className="mb-3 flex w-fit items-center rounded-md border border-line p-0.5"
            >
              {(['tabel', 'konsol'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    setTampilanForecast(v);
                    try {
                      localStorage.setItem(KUNCI_TAMPILAN_FORECAST, v);
                    } catch {
                      /* penyimpanan diblokir — pilihan tetap berlaku sesi ini */
                    }
                  }}
                  aria-pressed={tampilanForecast === v}
                  className={`min-h-tap rounded px-3 text-sm transition-colors ${
                    tampilanForecast === v
                      ? 'bg-subtle font-semibold text-accent-text'
                      : 'hover:bg-subtle'
                  }`}
                >
                  {v === 'tabel' ? t('Table') : t('Console')}
                </button>
              ))}
            </div>

            {tampilanForecast === 'konsol' && <KonsolForecast s={s} />}

            {tampilanForecast === 'tabel' && (
            <>
            {/* Tinggi dipatok ±5 baris, sisanya digulung **di dalam kartu**.
                Sebelumnya tabel ini tumbuh seturut jumlah kamera: pada 96
                kamera ia sendirian sepanjang ±3.500px, dan seluruh menu di
                bawahnya — Recording storage, Log retention, root rekaman —
                terdorong ke tempat yang tidak akan pernah digulung orang.
                Kartu yang isinya bisa panjang tak terbatas harus membatasi
                dirinya sendiri, bukan membebankan panjangnya ke halaman.

                Angkanya dipilih supaya baris keenam terlihat separuh: tepi
                yang terpotong rapi terbaca sebagai tabel yang habis, dan
                orangnya tidak pernah tahu masih ada 91 baris lagi di bawah. */}
            <div className="max-h-[15rem] overflow-auto rounded-md border border-line">
              <table className="w-full text-sm">
                {/* Kepala tabel ikut menempel saat digulung — tanpa ini, lima
                    baris pertama saja yang punya judul kolom.
                    Garis bawahnya `box-shadow`, bukan `border`: border pada sel
                    `position: sticky` tidak ikut dilukis di Chrome dan yang
                    tersisa cuma teks yang melayang di atas baris. */}
                <thead className="sticky top-0 z-10 bg-surface text-left">
                  <tr className="shadow-[inset_0_-1px_0_var(--color-line)]">
                    <th className="bg-surface py-2 pl-2 font-medium">Camera</th>
                    <th className="bg-surface py-2 font-medium">Measured bitrate</th>
                    <th className="bg-surface py-2 font-medium">GB per day</th>
                    <th className="bg-surface py-2 font-medium">Retention</th>
                    <th className="bg-surface py-2 pr-2 font-medium">Forecast</th>
                  </tr>
                </thead>
                <tbody>
                  {s.perCamera.map((c) => (
                    <tr key={c.cameraId} className="border-b border-line">
                      <td className="py-2 pl-2">
                        {c.name}
                        {!c.isEnabled && <span className="ml-2 text-xs text-muted">disabled</span>}
                      </td>
                      <td className="py-2 tabular-nums">
                        {c.measuredMbps === null ? '—' : `${c.measuredMbps} Mbps`}
                      </td>
                      <td className="py-2 tabular-nums">{c.gbPerDay ?? '—'}</td>
                      <td className="py-2 tabular-nums">
                        {c.maxDay} {c.maxDay === 1 ? 'day' : 'days'}
                        {/* Kamera yang mewarisi ditandai: angka yang sama bisa
                            berarti dua hal, dan yang ini ikut berubah sendiri
                            begitu setelan global diubah (§D6). */}
                        {c.maxDayInherited && (
                          <span className="ml-1.5 text-xs text-muted">global</span>
                        )}
                      </td>
                      <td className="py-2 pr-2 tabular-nums">
                        {c.projectedGb === null ? '—' : `${c.projectedGb} GB`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Totalnya duduk di luar kotak yang digulung — ia kesimpulan
                seluruh tabel, dan kesimpulan yang ikut menggulung sampai hilang
                dari layar adalah kesimpulan yang tidak dibaca siapa pun. */}
            <p className="mt-3 text-sm">
              <strong>{s.totalGbPerDay} GB per day</strong> in total, forecast{' '}
              <strong>{s.projectedGb} GB</strong>
              {s.perCamera.length > 5 && (
                <span className="text-muted"> · {s.perCamera.length} cameras, scroll for the rest</span>
              )}
            </p>
            </>
            )}
          </>
        )}
      </Section>

      {/* §D6 — dua setelan yang mengatur berapa lama rekaman hidup dan berapa
          banyak disk yang boleh dipakai. Ditaruh tepat di bawah proyeksi karena
          proyeksi itulah yang menjawab apakah angka di sini masuk akal. */}
      <Section title={t('Retention & capacity')}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (menyimpan || retensiKelewatan) return;
            void simpan({
              // Kosong berarti "kembalikan ke bawaan" untuk retensi, dan
              // "jangan dibatasi" untuk kuota. Keduanya dikirim sebagai `null`
              // yang eksplisit — field yang tidak dikirim berarti jangan
              // disentuh, dan itu keadaan yang berbeda (§D6).
              retentionDays: retensiRekamanDraft.trim() === '' ? null : Number(retensiRekamanDraft),
              maxDiskGb: kuotaDraft.trim() === '' ? null : Number(kuotaDraft),
              segmentSeconds: segmenDraft.trim() === '' ? null : Number(segmenDraft) * 60,
              spriteAutoScan: autoScanDraft,
            });
          }}
          className="flex flex-wrap items-end gap-4"
        >
          {/* §D6 — batasnya 30 hari, dan justru di sini pagarnya paling perlu:
              angka global menyeret setiap kamera yang mewarisi sekaligus, jadi
              satu ketikan salah di kotak ini berlaku untuk seluruh armada. */}
          <label className="flex flex-col gap-1 text-sm">
            {t('Global retention (days)')}
            <input
              type="number"
              min={1}
              max={RETENSI_MAX_HARI}
              value={retensiRekamanDraft}
              onChange={(e) => {
                setRetensiRekamanDraft(e.target.value);
                hapusPesan();
              }}
              placeholder={String(settings.editable.retentionDays.default)}
              aria-invalid={retensiKelewatan || undefined}
              className="min-h-tap w-44 rounded-md border border-line bg-surface px-3"
            />
            <span className={retensiKelewatan ? 'text-xs text-red-600' : 'text-xs text-muted'}>
              {t('Used by cameras that do not set their own.')}{' '}
              {t('At most {n} days — recordings older than that are removed by the storage server itself.', {
                n: RETENSI_MAX_HARI,
              })}
            </span>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            {t('Maximum disk capacity (GB)')}
            <input
              type="number"
              min={1}
              step="any"
              value={kuotaDraft}
              onChange={(e) => {
                setKuotaDraft(e.target.value);
                hapusPesan();
              }}
              placeholder={t('not limited')}
              className="min-h-tap w-44 rounded-md border border-line bg-surface px-3"
            />
            <span className="text-xs text-muted">
              {s.quota
                ? t('Oldest recordings are deleted above {n}%.', {
                    n: s.quota.thresholdPercent,
                  })
                : t('Leave empty to use the whole volume.')}
            </span>
          </label>

          {/* §D2 — panjang berkas rekaman. Duduk di sini, bukan di seksi
              tersendiri, karena ia setelan perekaman yang sama sifatnya dengan
              dua tetangganya: satu angka global yang bisa ditimpa per kamera. */}
          <label className="flex flex-col gap-1 text-sm">
            {t('Recording segment length (minutes)')}
            <input
              type="number"
              min={1}
              max={60}
              value={segmenDraft}
              onChange={(e) => {
                setSegmenDraft(e.target.value);
                hapusPesan();
              }}
              placeholder={String(Math.round(settings.editable.segmentSeconds.default / 60))}
              className="min-h-tap w-44 rounded-md border border-line bg-surface px-3"
            />
            <span className="text-xs text-muted">
              {t('Applies to the next segment — files being written now keep their length.')}
            </span>
          </label>

          <button
            type="submit"
            disabled={menyimpan || retensiKelewatan}
            className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast transition-[background-color,scale] duration-150 active:scale-[0.96] disabled:opacity-40"
          >
            {t('Save')}
          </button>
        </form>

        {/* Pemakaian jatah ditulis sebagai batang, bukan angka saja: yang perlu
            terbaca sekilas adalah "masih jauh" atau "sudah mepet", dan itu
            bentuk, bukan bilangan. Angkanya tetap ada di sebelahnya (§G3.1 —
            jangan pernah hanya lewat warna atau bentuk). */}
        {s.quota && (
          <div className="mt-4">
            <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
              <strong className="tabular-nums">
                {t('{dipakai} GB of {jatah} GB used', {
                  dipakai: s.quota.usedGb,
                  jatah: s.quota.gb,
                })}
              </strong>
              <span className="tabular-nums text-muted">({s.quota.usedPercent}%)</span>
              {s.quota.usedPercent >= s.quota.thresholdPercent && (
                <span className="text-amber-600">
                  {t('— above {n}%, the oldest recordings are being deleted', {
                    n: s.quota.thresholdPercent,
                  })}
                </span>
              )}
            </div>
            <div
              className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-subtle"
              role="img"
              aria-label={t('{n}% of the assigned disk capacity used', {
                n: s.quota.usedPercent,
              })}
            >
              <div
                className={`h-full ${
                  s.quota.usedPercent >= s.quota.thresholdPercent ? 'bg-amber-500' : 'bg-accent'
                }`}
                style={{ width: `${Math.min(100, s.quota.usedPercent)}%` }}
              />
            </div>
          </div>
        )}
      </Section>

      <Section title={t('Recording storage')}>
        {/* Yang menulis rekaman adalah worker, jadi hanya penilaiannya yang
            membuktikan folder ini bisa ditulis (§B4.1). Ditampilkan HANYA saat
            bermasalah: kalau sehat, tidak ada yang perlu diberitahukan. */}
        {health.recordingRoot.worker !== null && !health.recordingRoot.worker.ok && (
          <p className="mb-3 text-sm text-red-600" role="alert">
            {health.recordingRoot.worker.path} — {health.recordingRoot.worker.reason}
          </p>
        )}

        {/* <form>, bukan <div>. Tanpa ini Enter di kotak path tidak melakukan
            apa-apa: browser hanya mengirim form saat Enter kalau input-nya
            memang berada di dalam <form>. Yang terjadi bukan simpan gagal —
            request PUT tidak pernah terkirim sama sekali, jadi root lama
            bertahan tanpa satu pun pesan kesalahan. Terjadi 20 Agu 2026.
            Refleksnya pun dilatih aplikasi ini sendiri: Login dan form Kamera
            sudah <form> sejak awal, di sana Enter memang bekerja. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // Penjaga yang sama dengan `disabled` tombolnya. Sebagian browser
            // tetap meneruskan implicit submission meski tombol submit-nya
            // disabled, dan draft kosong akan ditolak Zod dengan 400.
            if (menyimpan || !rootDraft.trim()) return;
            void simpan({ recordingRoot: rootDraft.trim() });
          }}
          className="flex flex-wrap items-end gap-2"
        >
          <label className="flex flex-col gap-1 text-sm">
            {t('Recording folder')}
            <input
              type="text"
              value={rootDraft}
              onChange={(e) => {
                setRootDraft(e.target.value);
                hapusPesan();
              }}
              placeholder={settings.editable.recordingRoot.default}
              className="min-h-tap w-96 max-w-full rounded-md border border-line bg-surface px-3"
            />
          </label>
          <button
            type="submit"
            disabled={menyimpan || !rootDraft.trim()}
            className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast transition-[background-color,scale] duration-150 active:scale-[0.96] disabled:opacity-40"
          >
            {t('Save')}
          </button>
          <button
            type="button"
            disabled={menyimpan || settings.editable.recordingRoot.source === 'default'}
            onClick={() => void simpan({ recordingRoot: null })}
            className="min-h-tap rounded-md border border-line px-4 text-sm transition-[background-color,scale] duration-150 hover:bg-subtle active:scale-[0.96] disabled:opacity-40"
          >
            {t('Reset to default')}
          </button>
        </form>
      </Section>

      {/* Pemeliharaan aplikasi & storage (§D10.1) — setelan sistem (§C2.1).
          TIDAK dilipat: ini satu-satunya kartu di halaman ini yang keadaannya
          bisa membuat seluruh sistem berhenti melayani orang, dan kartu yang
          harus dibuka dulu untuk tahu ia sedang menyala adalah kartu yang akan
          ditinggalkan menyala. */}
      {/* Pratayang timeline (§D8.1). Ditaruh di antara penyimpanan dan
          pemeliharaan karena ia milik keduanya: yang dihasilkannya menempati
          NAS, dan menekan tombolnya adalah pekerjaan pemeliharaan. */}
      <Section title={t('Timeline previews')}>
        <p className="mb-3 text-sm text-muted">
          {t(
            'Fills in preview thumbnails for recordings that do not have one yet. Recordings that already have a preview are never rebuilt.',
          )}
        </p>
        {/* Sakelar auto scan (§D8.1). Disimpan lewat jalur `PUT /system/settings`
            yang sama dengan setelan lain, jadi ia ikut satu tombol Simpan —
            sakelar yang menyimpan sendiri di halaman yang punya tombol Simpan
            adalah dua aturan yang harus dipelajari untuk satu halaman. */}
        <label className="mb-3 flex min-h-tap items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={autoScanDraft}
            onChange={(e) => setAutoScanDraft(e.target.checked)}
          />
          <span>
            {t('Scan for missing thumbnails automatically')}
            <span className="block text-xs text-muted">
              {t(
                'Only works on cameras nobody is watching, and steps aside the moment someone opens their playback page. Off by default.',
              )}
            </span>
          </span>
        </label>

        <TombolIsiPratayang cameraId={null} label={t('Fill blank thumbnails — whole fleet')} />
        <p className="mt-3 text-xs text-muted">
          {t(
            'It runs slowly on purpose, a couple of previews at a time, so it never competes with recording. A full archive can take hours; you can leave this page.',
          )}
        </p>
      </Section>

      <Section title={t('Maintenance mode')}>
        <MaintenanceSwitch />
      </Section>

      {/* Setelan sistem, bukan urusan orang (§C2.1) — karena itu di sini, bukan
          di /users. Dilipat: isinya tumbuh seturut jumlah template, dan yang
          jadi isi halaman ini adalah setelan yang bisa diubah, bukan daftar. */}
      <Section
        title={t('Camera templates')}
        collapsible
        ringkas={t('Repeated settings, stored once — used when a camera is created')}
      >
        <CameraTemplates bolehUbah />
      </Section>

      <Section title={t('Camera log retention')}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (menyimpan || !retensiDraft.trim()) return;
            void simpan({ cameraLogRetentionDays: Number(retensiDraft) });
          }}
          className="flex flex-wrap items-end gap-2"
        >
          <label className="flex flex-col gap-1 text-sm">
            {t('Keep logs for (days)')}
            <input
              type="number"
              min={1}
              max={3650}
              value={retensiDraft}
              onChange={(e) => {
                setRetensiDraft(e.target.value);
                hapusPesan();
              }}
              placeholder={String(settings.editable.cameraLogRetentionDays.default)}
              className="min-h-tap w-40 rounded-md border border-line bg-surface px-3"
            />
          </label>
          <button
            type="submit"
            disabled={menyimpan || !retensiDraft.trim()}
            className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast transition-[background-color,scale] duration-150 active:scale-[0.96] disabled:opacity-40"
          >
            {t('Save')}
          </button>
          <button
            type="button"
            disabled={menyimpan || settings.editable.cameraLogRetentionDays.value === null}
            onClick={() => void simpan({ cameraLogRetentionDays: null })}
            className="min-h-tap rounded-md border border-line px-4 text-sm hover:bg-subtle disabled:opacity-40"
          >
            {t('Reset to default')}
          </button>
        </form>
      </Section>

      <Section title={t('Read-only configuration')}>
        {/* Ditampilkan tapi tidak bisa diisi. Field yang tampak bisa disimpan
            padahal tidak adalah kegagalan diam yang paling membingungkan. */}
        <dl className="text-sm">
          <Baris label={t('Hardware acceleration')}>
            {t('mode')} <code>{settings.readOnly.hwaccel.mode}</code>, {t('device')}{' '}
            <code>{settings.readOnly.hwaccel.device}</code>, {t('fallback')}{' '}
            {settings.readOnly.hwaccel.fallback ? t('on') : t('off')}
            {health.hwaccel && <> — {t('currently')} {health.hwaccel.summary}</>}
          </Baris>
          <Baris label={t('Streaming')}>
            {t('{n}s idle timeout, up to {maks} concurrent streams', {
              n: settings.readOnly.stream.idleTimeoutS,
              maks: settings.readOnly.stream.maxConcurrent,
            })}
          </Baris>
          <Baris label={t('LDAP')}>
            {settings.readOnly.ldap.enabled ? (
              <>
                {t('enabled')} — <code>{settings.readOnly.ldap.url}</code>,{' '}
                {t('{mode} mode', { mode: settings.readOnly.ldap.mode })}
              </>
            ) : (
              t('disabled')
            )}
          </Baris>
        </dl>
      </Section>

      <Section
        title={t('Audit log')}
        collapsible
        ringkas={
          !audit || audit.length === 0
            ? t('No activity recorded yet')
            : t('{n} most recent entries — last {waktu}', {
                n: audit.length,
                waktu: formatWib(audit[0]!.createdAt),
              })
        }
      >
        {!audit || audit.length === 0 ? (
          <p className="text-sm text-muted">{t('No activity recorded yet.')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left">
                <tr className="border-b border-line">
                  <th className="py-2 font-medium">{t('Time (WIB)')}</th>
                  <th className="py-2 font-medium">{t('User')}</th>
                  <th className="py-2 font-medium">{t('Action')}</th>
                  <th className="py-2 font-medium">{t('Object')}</th>
                  <th className="py-2 font-medium">{t('IP')}</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-b border-line">
                    <td className="py-2">{formatWib(a.createdAt)}</td>
                    <td className="py-2">{a.username}</td>
                    <td className="py-2">{a.action}</td>
                    <td className="py-2 text-muted">
                      {a.tableName}
                      {a.recordId && ` #${a.recordId}`}
                    </td>
                    <td className="py-2 text-muted">{a.ipAddress}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}

function Baris({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2 border-t border-line py-2 first:border-t-0">
      <dt className="w-56 shrink-0 text-muted">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
